import { AnalysisDetails } from '@/components/ui/AnalysisDetails'
import { EnhancementGoals } from '@/components/ui/EnhancementGoals'
import { EnhancementSuggestions } from '@/components/ui/EnhancementSuggestions'
import { useEffect, useMemo, useState } from 'react'
import { ResponseType } from '@/api/avpulse-api'
import {
  getImageEnhancementSuggestions,
  generateEnhancedImage,
} from '@/api/avpulse-api'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'
import { useTracking } from '@/hooks/useTracking'
import { toast } from 'sonner'
import { selectMediaUri } from '@/store/mediaSlice'
import { useSelector } from 'react-redux'

interface ParsedExplanation {
  summary: string
  myra_score: string
  visual_journey: {
    initial_pull_hierarchy: string
    gaze_path_and_flow: string
  }
  emotional_landscape: {
    emotional_state: string
    emotional_and_brand_resonance: string
  }
  socio_political: {
    representation: string
    symbolism_and_archetypes: string
    brand_values_and_associations: string
    implied_narrative_and_dynamics: string
  }
  strategic_refinements: {
    composition_and_lighting_strategy: string
    gaze_fix_suggestions: string
    emotional_and_narrative_shift_suggestions: string
    tempering_graphic_content: string
  }
  questions: string[]
}

interface EnhancementSuggestion {
  visual_element: string
  visual_changes: string
  expected_impact: string
  implementation_priority: number
}

export const AnalysisExplanation = ({
  explanation,
  explanationLoading,
  conversationId,
  responseType,
  runId,
}: {
  explanation: string
  explanationLoading: boolean
  conversationId?: string
  responseType?: ResponseType
  runId?: string
}) => {
  const [userResponse, setUserResponse] = useState('')
  const [enhancementSuggestions, setEnhancementSuggestions] = useState<
    EnhancementSuggestion[]
  >([])
  const [currentResponseType, setCurrentResponseType] = useState<
    ResponseType | undefined
  >(responseType)

  const [explanationVersion, setExplanationVersion] = useState<number>(1)
  const [suggestionsLoading, setSuggestionsLoading] = useState<boolean>(false)
  const { trackEnhancementSuggestions } = useTracking()
  const [enhancedImage, setEnhancedImage] = useState<string | null>(null)
  const [enhancedImageLoading, setEnhancedImageLoading] =
    useState<boolean>(false)
  const imageUri = useSelector(selectMediaUri) as string
  const [isEnhancedImageOpen, setIsEnhancedImageOpen] = useState<boolean>(true)

  const handleGenerateEnhancedImage = () => {
    if (!conversationId || !runId) return
    setEnhancedImageLoading(true)
    generateEnhancedImage(runId, conversationId, imageUri)
      .then(res => {
        // Handle both URL and base64 image strings
        const imageData = res.response
        if (imageData) {
          setEnhancedImage(imageData)
          setIsEnhancedImageOpen(true)
          setEnhancedImageLoading(false)
        } else {
          toast.error('Could not generate enhanced image. Please try again.')
          setEnhancedImageLoading(false)
        }
      })
      .catch(() => {
        toast.error(
          'Failed to generate enhanced image. Please try again later.',
        )
        setEnhancedImageLoading(false)
      })
  }

  const handleSubmitUserResponse = async () => {
    if (!conversationId || !runId) return
    setSuggestionsLoading(true)
    trackEnhancementSuggestions(runId, conversationId)
    try {
      const res = await getImageEnhancementSuggestions(
        runId,
        conversationId,
        userResponse,
      )

      const parsed = JSON.parse(
        res.response.replace('```json', '').replace('```', ''),
      )
      setEnhancementSuggestions(parsed)
      setCurrentResponseType(ResponseType.ENHANCEMENT_SUGGESTIONS)
    } catch (err) {
      console.error(err)
      setCurrentResponseType(ResponseType.EXPLANATION_WITH_QUESTION)
    } finally {
      setSuggestionsLoading(false)
    }
  }

  useEffect(() => {
    setCurrentResponseType(responseType)
  }, [responseType])


  const parsedExplanation = useMemo(() => {
    if (!explanation) return null
    if (explanation.includes('```json') || explanation.startsWith('{')) {
      setExplanationVersion(2)
      const jsonString = explanation.includes('```json') 
        ? explanation.trim()
            .replace('&nbsp; \n', '\n')
            .replace('```json', '')
            .replace('```', '')
          : explanation.trim()
          
      return JSON.parse(jsonString)
    } else {
      setExplanationVersion(1)
      return explanation
    }
  }, [explanation]) as ParsedExplanation

  return (
    <div className="grid grid-cols-1 gap-4">
      {explanationLoading && (
        <div className="text-sm mb-4 mt-4">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 mb-2"></div>
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-5/6"></div>
          </div>
        </div>
      )}
      {!explanationLoading && (
        <div className="text-sm mb-4 mt-4">
          {/* Preview text */}
          <div
            className="prose prose-sm max-w-none prose-gray dark:prose-invert prose-headings:text-gray-900 
          dark:prose-headings:text-gray-100 prose-p:text-gray-800 dark:prose-p:text-gray-200 prose-strong:text-gray-900 
          dark:prose-strong:text-gray-100 prose-a:text-primary hover:prose-a:text-primary/80"
          >
            {explanationVersion === 1 && (
              <div>
                <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                  {explanation}
                </ReactMarkdown>
              </div>
            )}
            {explanationVersion === 2 && parsedExplanation && (
              <div>
                <AnalysisDetails parsedExplanation={parsedExplanation} />
                {/* Enhancement Goals */}
                {currentResponseType ===
                  ResponseType.EXPLANATION_WITH_QUESTION && (
                  <EnhancementGoals
                    questions={parsedExplanation.questions}
                    userResponse={userResponse}
                    onUserResponseChange={setUserResponse}
                    onSubmit={handleSubmitUserResponse}
                    isLoading={suggestionsLoading}
                  />
                )}
                {/* Enhancement Suggestions */}
                {currentResponseType ===
                  ResponseType.ENHANCEMENT_SUGGESTIONS && (
                  <EnhancementSuggestions
                    suggestions={enhancementSuggestions}
                    userGoals={userResponse}
                    enhancedImage={enhancedImage}
                    enhancedImageLoading={enhancedImageLoading}
                    isEnhancedImageOpen={isEnhancedImageOpen}
                    onGenerateEnhancedImage={handleGenerateEnhancedImage}
                    onEnhancedImageOpenChange={setIsEnhancedImageOpen}
                  />
                )}
              </div>
            )}
            {!explanation && (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                No explanation available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
